import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { RootState } from 'app/store';
import { UploadCatalogForm } from '../models';
import InputText from 'components/TextInput/InputText';
import TextArea from 'components/TextArea/TextArea';
import BasicSelectInput, {
    Option,
} from 'components/SelectInput/BasicSelectInput';
import SingleFileDropzone from 'components/FileInput/SingleDropzone';
import Button from 'components/Button/Button';
import { IoMdClose } from 'react-icons/io';
import { RiLock2Line, RiUploadCloud2Line } from 'react-icons/ri';
import { uploadNewCatalog } from '../catalog.reducer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { UserCredential } from 'features/login/models';
import { getList as getTeamList } from 'features/master/team/team.reducer';

interface UploadCatalogModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const schema = yup.object().shape({
    document_title: yup.string().required('Document Title is required'),
    category: yup
        .string()
        .required('Category is required')
        .notOneOf([''], 'Please select a category'),
    group_id: yup
        .string()
        .required('Team is required')
        .notOneOf([''], 'Please select a team'),
    description: yup
        .string()
        .required('Description / Change summary is required'),
    file: yup.mixed().required('Please attach a file to upload'),
});

const CATEGORY_OPTIONS: Option[] = [
    { value: '', text: 'Select category' },
    { value: '0', text: 'SOP (Standard Operating Procedure)' },
    { value: '3', text: 'Work Instruction' },
    { value: '2', text: 'Company Policy' },
    { value: '1', text: 'Training Material' },
    { value: '4', text: 'OCALC Catalogue' },
    { value: '5', text: 'Template PPT' },
];

const UploadCatalogModal: React.FC<UploadCatalogModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);

    const user = useAppSelector(
        (state: RootState) => state.user.userAuth as UserCredential,
    );
    const teamOptions = useAppSelector(
        (state: RootState) => state.team.options ?? [],
    );
    const isLoading = useAppSelector(
        (state: RootState) => state.catalog.isLoading ?? false,
    );
    const uploadProgress = useAppSelector(
        (state: RootState) => state.catalog.uploadProgress ?? 0,
    );

    // const canUpload = user?.roleName?.some(
    //     (r) => r.toUpperCase().includes('SPV') || r.toLowerCase() === 'admin' || r.toLowerCase() === 'administrator'
    // ) || false;

    const canUpload = true;

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm<UploadCatalogForm>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            document_title: '',
            category: '',
            group_id: '',
            description: '',
            file: undefined,
        },
    });

    const fileWatcher = watch('file');

    useEffect(() => {
        if (isOpen && teamOptions.length === 0) {
            dispatch(getTeamList({ skip: 0, take: 1000 }));
        }
    }, [isOpen, teamOptions.length, dispatch]);

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const onFileChange = (file: File) => {
        setValue('file', file, { shouldValidate: true });
    };

    const onSubmit = async (data: UploadCatalogForm) => {
        const selectedTeam = teamOptions.find(
            (d) => d.value === data.group_id,
        );
        const groupName = selectedTeam
            ? selectedTeam.text
            : `Group ${data.group_id}`;

        const res = await dispatch(uploadNewCatalog({ form: data, groupName }));
        if (uploadNewCatalog.fulfilled.match(res)) {
            MySwal.fire({
                title: 'Uploaded Successfully!',
                text: `"${data.document_title}" has been uploaded with version V1.0 and status Pending Review.`,
                icon: 'success',
                confirmButtonColor: '#4c8b6a',
            });
            reset();
            if (onSuccess) onSuccess();
            onClose();
        } else {
            MySwal.fire({
                title: 'Upload Failed',
                text: 'There was an error uploading the document. Please try again.',
                icon: 'error',
            });
        }
    };

    if (!isOpen) return null;

    const groupOptionsWithDefault: Option[] = [
        { value: '', text: 'Select team' },
        ...teamOptions,
    ];

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-end bg-black/50 backdrop-blur-sm transition-opacity duration-300">
            <div className="bg-white dark:bg-gray-800 w-full max-w-lg h-full overflow-y-auto shadow-2xl flex flex-col justify-between animate-slide-left border-l border-gray-100 dark:border-gray-700">
                {/* Header */}
                <div>
                    <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur z-10">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                                <RiUploadCloud2Line
                                    className="mr-2 text-[#4c8b6a]"
                                    size={24}
                                />
                                Upload Catalog
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Upload a new document to the catalog
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <IoMdClose size={22} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {!canUpload ? (
                            <div className="p-8 text-center bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-200 dark:border-amber-800 my-6">
                                <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl shadow-inner">
                                    <RiLock2Line />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                                    Access Restricted
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    Only users with the{' '}
                                    <strong className="text-amber-600 dark:text-amber-400">
                                        SPV
                                    </strong>{' '}
                                    role can upload new documents to the
                                    catalog. Please contact your administrator
                                    if you believe this is an error.
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="space-y-4"
                            >
                                {/* Document Title */}
                                <InputText
                                    label="Document Title *"
                                    placeholder="Enter document title"
                                    {...register('document_title')}
                                    message={errors.document_title?.message}
                                />

                                {/* Category */}
                                <BasicSelectInput
                                    label="Category *"
                                    options={CATEGORY_OPTIONS}
                                    {...register('category')}
                                    message={errors.category?.message}
                                />

                                {/* Team */}
                                <BasicSelectInput
                                    label="Team"
                                    options={groupOptionsWithDefault}
                                    {...register('group_id')}
                                    message={errors.group_id?.message}
                                />

                                {/* Auto-generated Version & Status row */}
                                <div className="grid grid-cols-2 gap-4 pt-1">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Version *
                                        </label>
                                        <div className="p-2.5 bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-300 font-semibold flex items-center justify-between">
                                            <span>V1.0</span>
                                            <span className="text-[10px] bg-[#4c8b6a]/15 text-[#4c8b6a] px-2 py-0.5 rounded font-bold">
                                                Auto
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            Generated by backend
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Initial Status
                                        </label>
                                        <div className="p-2.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg text-sm text-amber-700 dark:text-amber-300 font-semibold flex items-center justify-between">
                                            <span className="flex items-center">
                                                <span className="w-2 h-2 rounded-full bg-amber-500 mr-2" />
                                                Pending Review
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            Requires Head approval
                                        </p>
                                    </div>
                                </div>

                                {/* Description / Change Summary */}
                                <TextArea
                                    label="Description / History Perubahan *"
                                    placeholder="Enter document description or summary of changes..."
                                    rows={4}
                                    {...register('description')}
                                    message={errors.description?.message}
                                />

                                {/* File Upload */}
                                <div className="pt-2">
                                    <SingleFileDropzone
                                        label="File Upload *"
                                        onChange={onFileChange}
                                        file={fileWatcher as File}
                                        uploadProgress={uploadProgress}
                                        message={errors.file?.message as string}
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        Supported formats: PDF, DOC, DOCX, XLS,
                                        XLSX, PPT, PPTX (Max 50MB)
                                    </p>
                                </div>

                                {/* Footer buttons inside form */}
                                <div className="pt-6 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end space-x-3 sticky bottom-0 bg-white dark:bg-gray-800 pb-2">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="px-5 py-2.5 rounded-lg bg-[#4c8b6a] hover:bg-[#3d7055] text-white font-medium text-sm transition-colors flex items-center justify-center disabled:opacity-50"
                                    >
                                        {isLoading
                                            ? 'Uploading...'
                                            : 'Upload Document'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>

                {!canUpload && (
                    <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex justify-end bg-gray-50 dark:bg-gray-800/50">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2.5 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium text-sm hover:bg-gray-300 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadCatalogModal;
